import { Component, Input, OnDestroy } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { ApprovalHistoryRecord } from '../../../raci/models/approval-history-record';
import { GroupApprovalHistory } from '../../../raci/models/group-approval-history';
import { RaciService } from '../../../raci/services/raci.service';
import { TaskTypeEnum } from '../../models/task-type-enum';

/**
 * Handles the approval history component
 */
@Component({
  selector: 'approval-history',
  templateUrl: './raci-approval-history.component.html',
  styleUrls: ['./raci-approval-history.component.css']
})
export class ApprovalHistoryComponent implements OnDestroy {
  /**
  * Array of subscriptions.
  */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
   * The general tasks approval history.
   */
  generalTasksApprovalHistory: any[];

  /**
   * The task.
   */
  task: any;

  /**
   * The approval history.
   */
  approvalHistory: ApprovalHistoryRecord[] = [];

  /**
   * Array of grouped approval history data by properties.
   */
  groupByPropertiesData: Array<GroupApprovalHistory> = [];

  /**
   * Array of grouped approval history data by date.
   */
  groupByDateData: Array<GroupApprovalHistory> = [];

  /**
   * Array of grouped approval history data by project.
   */
  groupByProjectData: Array<GroupApprovalHistory> = [];

  /**
   * The grouping property.
   */
  groupBy: string = null;

  /**
   * Flag to show/hide the close icon.
   */
  showCloseIcon = true;

  /**
   * The Valorem history.
   */
  valoremHistory: any;

  /**
   * The portfolio ID.
   */
  portfolioId: any;

  /**
   * Sets the active task.
   */
  @Input() set activeTask(value: any) {
    if (value) {
      this.setTaskHistory(value);
    }
  }

  /**
   * Sets the general task history.
   */
  @Input() set generalTaskHistory(value: any[]) {
    if (value) {
      this.setGeneralTaskHistory(value);
    }
  }

  /**
 * OnDestroy
 * */
  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => { subscription.unsubscribe() });
  }

  /**
   * Constructor
   * @param _raciService
   * @param _ais
   * @param _raciNavigationService
   */
  constructor(private _raciService: RaciService, private _raciNavigationService: RaciNavigationService) { }

  /**
   * Map of history data
   */
  mapHistoryData() {
    this._subscriptions.push(this.getData().subscribe(data => {
      if (data) {
        this.approvalHistory = [];
        data[0].forEach(approval => { if (approval.dateOfChange) this.approvalHistory.push(new ApprovalHistoryRecord(approval)) });
        this.filterData();
        this.valoremHistory = data[1];
      }
    }));
  }

  /**
  * Gets the Action Stage enum
  */
  public get taskTypes(): typeof TaskTypeEnum {
    return TaskTypeEnum;
  }

  /**
   * Maps general task history data
   * */
  mapGeneralTasksHistoryData() {
    if (this.generalTasksApprovalHistory) {
      this.generalTasksApprovalHistory.forEach(approval => {
        approval.isGeneralHistory = true;
        this.approvalHistory.push(new ApprovalHistoryRecord(approval));
      });
      this.filterGeneralHistoryData();
    }
  }

  /**
   * Filters general task history data
   * */
  filterGeneralHistoryData() {
    this.resetGroupByData();
    this.groupBy = 'project';
    this.groupByProject();
    this.groupByDate();
  }

  /**
   * Filters the history data based on the grouping option "property".
   */
  filterData() {
    this.resetGroupByData();
    this.groupBy = 'property';
    this.groupByProperties();
    this.groupByDate();
  }

  /**
   * Resets group by configuration
   * */
  resetGroupByData() {
    this.groupByProjectData = [];
    this.groupByPropertiesData = [];
    this.groupByDateData = [];
  }

  /**
   * Groups the history data by properties.
   */
  groupByProperties() {
    this.approvalHistory.forEach(approval => this.mapHistoryByProperties(approval))
  }

  /**
   * Groups the history data by date.
   */
  groupByDate() {
    this.approvalHistory.forEach(approval => this.mapHistoryByDate(approval))
  }

  /**
   * Groups the history data by project.
   */
  groupByProject() {
    this.approvalHistory.forEach(approval => this.mapHistoryByProject(approval))
  }

  /**
   * Maps approval data in order to display the data grouped by project
   * @param approval Approval data
   */
  mapHistoryByProject(approval: any) {
    let approvalHistory = this.groupByProjectData.find(item => item.identifier == approval.projectOpportunityId)
    if (approvalHistory) {
      approvalHistory.historyRecords.push(approval);
    } else {
      approvalHistory = new GroupApprovalHistory({ group: approval.projectName, identifier: approval.projectOpportunityId, historyRecords: [approval] });
      this.groupByProjectData.push(approvalHistory);
    }
  }

  /**
  * Maps approval data in order to display the data grouped by property
  * @param approval Approval data
  */
  mapHistoryByProperties(approval: any) {
    let approvalHistory = this.groupByPropertiesData.find(item => item.group == approval.property)
    if (approvalHistory) {
      approvalHistory.historyRecords.push(approval);
    } else {
      approvalHistory = new GroupApprovalHistory({ group: approval.property, historyRecords: [approval] });
      this.groupByPropertiesData.push(approvalHistory);
    }
  }

  /**
  * Maps approval data in order to display the data grouped by date
  * @param approval Approval data
  */
  mapHistoryByDate(approval: any) {
    let approvalHistory = this.groupByDateData.find(item => item.group == approval.dateOfChange.toLocaleDateString())
    if (approvalHistory) {
      approvalHistory.historyRecords.push(approval);
    } else {
      approvalHistory = new GroupApprovalHistory({ group: approval.dateOfChange.toLocaleDateString(), historyRecords: [approval] });
      this.groupByDateData.push(approvalHistory);
    }
  }

  /**
   * Retrieves service data
   * */
  getData() {
    const taskId = this.task.projectTaskId ?? this.task.taskId;
    const approvalHistory = this._raciService.getApprovalHistory(taskId);
    const valoremHistory = this.portfolioId ? this._raciService.getValoremHistory(this.portfolioId) : [];

    return forkJoin([approvalHistory, valoremHistory]);
  }

  /**
   * Updates the group by parameter
   * @param groupBy Desired gorup by parameter
   */
  changeGroupStatus(groupBy: string) {
    this.groupBy = groupBy;
  }

  /**
   * On close approval history event
   * */
  closeApprovalHistory() {
    this._raciNavigationService.onDisplayApprovalHistory(false);
    this._raciNavigationService.onDisplayTaskApprovalHistoryDialog(false);
  }

  /**
   * Sets task history data
   * @param task Task information
   */
  setTaskHistory(task: any) {
    this.task = task;
    this.portfolioId = task.portfolioId ?? task.taskValuationPortfolioId;
    this.showCloseIcon = false;
    this.approvalHistory = [];
    this.mapHistoryData();
  }

  /**
  * Sets general task history data
  * @param generalTask Task information
  */
  setGeneralTaskHistory(generalTask: any) {
    this.showCloseIcon = true;
    const filteredByGeneralTaskId = [];
    generalTask.forEach(x => filteredByGeneralTaskId.filter(a => a.generalTaskOid == x.generalTaskOid).length > 0 ? null : filteredByGeneralTaskId.push(x));
    this.generalTasksApprovalHistory = filteredByGeneralTaskId;
    this.mapGeneralTasksHistoryData();
  }
}
